import { useEffect, useRef, useState } from "react";
import { useTheme } from "../ThemeProvider";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";
import YesNoField from "./YesNoField";
import { filtrarOpcionesAire, filtrarOpcionesCalefaccion } from "../../utils/fichaCaptacionAux";
import GarajePlazasButtons, {
  buildGarajePlazasPatch,
  parseNumPlazasGaraje,
} from "./GarajePlazasButtons";
import ModalUrbanismo, { urbanismoTieneDatos } from "./ModalUrbanismo";

const GARAJE_TIPOS = [
  { key: "abierto", label: "Abierto" },
  { key: "cerrado", label: "Cerrado" },
  { key: "cubierto", label: "Cubierto" },
  { key: "interior", label: "Interior" },
  { key: "exterior", label: "Exterior" },
];

/** Parejas excluyentes (misma regla que APP / ModalVinculosAgente). */
const GARAJE_TIPO_EXCLUSIVOS = {
  abierto: ["cerrado"],
  cerrado: ["abierto"],
  interior: ["exterior"],
  exterior: ["interior"],
};

function toggleGarajeTipoExclusivo(selected, val) {
  const v = String(val || "").toLowerCase();
  if (!v) return Array.isArray(selected) ? selected.slice() : [];
  const set = new Set((selected || []).map((x) => String(x).toLowerCase()));
  if (set.has(v)) set.delete(v);
  else {
    set.add(v);
    (GARAJE_TIPO_EXCLUSIVOS[v] || []).forEach((r) => set.delete(r));
  }
  return GARAJE_TIPOS.map(({ key }) => key).filter((k) => set.has(k));
}

function garajeTiposArray(form) {
  const raw = form.garaje_tipos;
  if (Array.isArray(raw)) return raw.map((x) => String(x).toLowerCase());
  if (raw == null || raw === "") return [];
  return String(raw).split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
}

function formatGarajeTiposText(selectedKeys, tv = (x) => x) {
  const labels = GARAJE_TIPOS
    .filter(({ key }) => selectedKeys.includes(key))
    .map(({ label }) => tv(label));
  if (labels.length === 0) return tv("Tipo garaje...");
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} ${tv("y")} ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} ${tv("y")} ${labels[labels.length - 1]}`;
}

function valorNumeroPlaza(form, name) {
  const v = form[name];
  return v != null && v !== "" ? String(v) : "";
}

function DesplegableTiposGaraje({ selected, onChange, inputStyle, isDark, c, disabled = false, tv = (x) => x }) {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggle = (key) => {
    if (disabled) return;
    onChange(toggleGarajeTipoExclusivo(selected, key));
  };

  const texto = formatGarajeTiposText(selected, tv);

  return (
    <div ref={ref} style={{ position: "relative", minWidth: 160 }}>
      <button
        type="button"
        onClick={() => { if (!disabled) setAbierto((v) => !v); }}
        disabled={disabled}
        title={disabled ? "No tienes permiso para modificar" : undefined}
        style={{
          ...inputStyle,
          width: "100%",
          minWidth: 160,
          textAlign: "left",
          cursor: disabled ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          color: selected.length ? c.textPrimary : c.textMuted,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{texto}</span>
        <span style={{ fontSize: 10, opacity: 0.7 }}>{abierto ? "▲" : "▼"}</span>
      </button>
      {abierto && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 50,
            minWidth: "100%",
            background: isDark ? "#111827" : "#fff",
            border: `1px solid ${c.chipBorder}`,
            borderRadius: 8,
            boxShadow: isDark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(0,0,0,0.12)",
            padding: "6px 0",
          }}
        >
          {GARAJE_TIPOS.map(({ key, label }) => {
            const marcado = selected.includes(key);
            return (
              <label
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: 12,
                  color: c.textPrimary,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  onChange={() => toggle(key)}
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    accentColor: c.chipActiveBorder || "#2563eb",
                    borderRadius: "50%",
                    cursor: "pointer",
                  }}
                />
                {tv(label)}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EquipamientoChip({ name, label, activo, chipStyle, onEnter, onLeave, onToggle, disabled = false, tv = (x) => x }) {
  return (
    <button type="button" onClick={() => { if (!disabled) onToggle(); }}
      disabled={disabled}
      title={disabled ? "No tienes permiso para modificar" : undefined}
      style={{ ...chipStyle(activo), cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      onMouseEnter={e => !disabled && onEnter(e, activo)}
      onMouseLeave={e => !disabled && onLeave(e, activo)}
    >
      {tv(label)}
    </button>
  );
}

function EquipamientoChipConSelect({
  name, label, selData, selName, form, cambiar, cambiarDirecto, selectStyle,
  chipStyle, onEnter, onLeave, fieldLabelStyle, controlRowHeight, disabled = false, tv = (x) => x,
}) {
  const rawBool = form[name];
  const idVal = selName ? form[selName] : "";
  const tieneTipo = idVal != null && String(idVal).trim() !== "";
  const marcadoNo = rawBool === false || rawBool === "false" || rawBool === 0 || rawBool === "0";
  // Ampliar: APK select_native a veces deja solo *_id → CRM debe mostrar Sí + tipo.
  const marcadoSi =
    rawBool === true ||
    rawBool === "true" ||
    rawBool === 1 ||
    rawBool === "1" ||
    (tieneTipo && !marcadoNo);

  useEffect(() => {
    if (disabled || typeof cambiarDirecto !== "function") return;
    if (!tieneTipo || marcadoNo) return;
    if (rawBool === true || rawBool === "true" || rawBool === 1 || rawBool === "1") return;
    cambiarDirecto(name, true);
  }, [disabled, tieneTipo, marcadoNo, rawBool, name, cambiarDirecto]);

  const formYes = marcadoSi && rawBool !== true ? { ...form, [name]: true } : form;

  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start" }}>
      <span style={fieldLabelStyle}>{tv(label)}</span>
      <div style={{ display: "flex", gap: 6, alignItems: "center", minHeight: controlRowHeight }}>
        <YesNoField
          name={name}
          form={formYes}
          cambiarDirecto={cambiarDirecto}
          compact
          chipStyle={chipStyle}
          onEnter={onEnter}
          onLeave={onLeave}
          disabled={disabled}
          onChangeSideEffect={(v) => { if (v !== true && selName) cambiarDirecto(selName, ""); }}
        />
        {marcadoSi && selData && selData.length > 0 && (
          <select name={selName} value={form[selName] || ""} onChange={cambiar} disabled={disabled}
            style={{ ...selectStyle, maxWidth: 168, minWidth: 120, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
            <option value="">{tv("Tipo...")}</option>
            {selData.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

function CampoTrastero({ form, cambiarDirecto, chipStyle, onEnter, onLeave, inputStyle, disabled = false, tv = (x) => x }) {
  const activo = !!form.eq_trastero;
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <EquipamientoChip
        label="Trastero"
        activo={activo}
        chipStyle={chipStyle}
        onEnter={onEnter}
        onLeave={onLeave}
        disabled={disabled}
        onToggle={() => {
          cambiarDirecto("eq_trastero", !activo);
          if (activo) cambiarDirecto("num_trastero", "");
        }}
      />
      {activo && (
        <input
          type="text"
          value={valorNumeroPlaza(form, "num_trastero")}
          onChange={(e) => cambiarDirecto("num_trastero", e.target.value.slice(0, 80))}
          placeholder={tv("Nº trastero")}
          maxLength={80}
          autoComplete="off"
          disabled={disabled}
          style={{ ...inputStyle, width: 120, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
        />
      )}
    </div>
  );
}

function CampoGaraje({ form, cambiarDirecto, chipStyle, onEnter, onLeave, inputStyle, isDark, c, disabled = false, tv = (x) => x }) {
  const plazas = parseNumPlazasGaraje(form);
  const activo = plazas != null && plazas > 0;
  const tiposSeleccionados = garajeTiposArray(form);
  const detalleRaw = Array.isArray(form.garajes_plazas_detalle) ? form.garajes_plazas_detalle : null;
  const usarDetalle = activo && (plazas >= 2 || (detalleRaw && detalleRaw.length > 0));

  const detalle = (() => {
    if (!usarDetalle) return [];
    const n = Math.max(1, plazas || (detalleRaw ? detalleRaw.length : 1));
    const base = Array.isArray(detalleRaw) ? detalleRaw.slice() : [];
    while (base.length < n) base.push({ tipos: [], numero: "" });
    return base.slice(0, n).map((pl) => ({
      tipos: Array.isArray(pl?.tipos) ? pl.tipos.map((x) => String(x).toLowerCase()) : [],
      numero: pl?.numero != null ? String(pl.numero) : "",
    }));
  })();

  const aplicarAgregadosDesdeDetalle = (nextDetalle) => {
    const tiposSet = [];
    const seen = {};
    const nums = [];
    for (const pl of nextDetalle) {
      for (const t of pl.tipos || []) {
        const k = String(t).toLowerCase();
        if (!seen[k]) { seen[k] = 1; tiposSet.push(k); }
      }
      if (pl.numero != null && String(pl.numero).trim() !== "") {
        nums.push(String(pl.numero).trim());
      }
    }
    cambiarDirecto("garajes_plazas_detalle", nextDetalle);
    cambiarDirecto("garaje_tipos", tiposSet);
    cambiarDirecto("num_garaje", nums.join(", "));
  };

  const aplicarPlazas = (n) => {
    if (disabled) return;
    const patch = buildGarajePlazasPatch(n);
    Object.entries(patch).forEach(([k, v]) => cambiarDirecto(k, v));
    if (n <= 0) {
      cambiarDirecto("garajes_plazas_detalle", null);
      return;
    }
    if (n >= 2) {
      const prev = Array.isArray(form.garajes_plazas_detalle) ? form.garajes_plazas_detalle : [];
      const next = [];
      for (let i = 0; i < n; i++) {
        next.push(prev[i] && typeof prev[i] === "object"
          ? {
              tipos: Array.isArray(prev[i].tipos) ? prev[i].tipos : [],
              numero: prev[i].numero != null ? String(prev[i].numero) : "",
            }
          : { tipos: [], numero: "" });
      }
      // Si no había detalle y hay agregados legacy, sembrar plaza 1
      if (!prev.length && n >= 1) {
        const tiposLegacy = garajeTiposArray(form);
        const numLegacy = valorNumeroPlaza(form, "num_garaje");
        if (tiposLegacy.length || numLegacy) {
          next[0] = { tipos: tiposLegacy, numero: numLegacy };
        }
      }
      aplicarAgregadosDesdeDetalle(next);
    } else {
      // 1 plaza: mantener UI simple; detalle opcional de 1
      const tipos = garajeTiposArray(form);
      const num = valorNumeroPlaza(form, "num_garaje");
      cambiarDirecto("garajes_plazas_detalle", [{ tipos, numero: num }]);
    }
  };

  const setPlazaTipos = (idx, tipos) => {
    const next = detalle.map((pl, i) => (i === idx ? { ...pl, tipos } : pl));
    aplicarAgregadosDesdeDetalle(next);
  };
  const setPlazaNumero = (idx, numero) => {
    const next = detalle.map((pl, i) => (i === idx ? { ...pl, numero: String(numero).slice(0, 80) } : pl));
    aplicarAgregadosDesdeDetalle(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
      <span style={{
        fontSize: 11, fontWeight: 600, color: c.textMuted,
        letterSpacing: "0.02em", lineHeight: 1.2,
      }}>
        {tv("Garaje / Parking (plazas)")}
      </span>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", width: "100%" }}>
        <GarajePlazasButtons
          value={plazas}
          onChange={aplicarPlazas}
          chipStyle={chipStyle}
          onEnter={onEnter}
          onLeave={onLeave}
          disabled={disabled}
        />
        {activo && !usarDetalle && (
          <>
            <DesplegableTiposGaraje
              selected={tiposSeleccionados}
              onChange={(next) => {
                cambiarDirecto("garaje_tipos", next);
                cambiarDirecto("garajes_plazas_detalle", [{
                  tipos: next,
                  numero: valorNumeroPlaza(form, "num_garaje"),
                }]);
              }}
              inputStyle={inputStyle}
              isDark={isDark}
              c={c}
              disabled={disabled}
              tv={tv}
            />
            <input
              type="text"
              value={valorNumeroPlaza(form, "num_garaje")}
              onChange={(e) => {
                const v = e.target.value.slice(0, 80);
                cambiarDirecto("num_garaje", v);
                cambiarDirecto("garajes_plazas_detalle", [{
                  tipos: tiposSeleccionados,
                  numero: v,
                }]);
              }}
              placeholder={tv("Nº garaje")}
              maxLength={80}
              autoComplete="off"
              disabled={disabled}
              style={{ ...inputStyle, width: 120, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
            />
          </>
        )}
      </div>
      {usarDetalle && detalle.map((pl, idx) => (
        <div
          key={`plaza-${idx}`}
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexWrap: "wrap",
            width: "100%",
            padding: "6px 8px",
            borderRadius: 8,
            border: `1px solid ${c.chipBorder}`,
            background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, minWidth: 64 }}>
            {tv("Plaza")} {idx + 1}
          </span>
          <DesplegableTiposGaraje
            selected={pl.tipos}
            onChange={(next) => setPlazaTipos(idx, next)}
            inputStyle={inputStyle}
            isDark={isDark}
            c={c}
            disabled={disabled}
            tv={tv}
          />
          <input
            type="text"
            value={pl.numero}
            onChange={(e) => setPlazaNumero(idx, e.target.value)}
            placeholder={tv("Nº garaje")}
            maxLength={80}
            autoComplete="off"
            disabled={disabled}
            style={{ ...inputStyle, width: 120, opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "text" }}
          />
        </div>
      ))}
    </div>
  );
}

export default function SeccionEquipamiento(props) {
  const { form, cambiar, cambiarDirecto, auxiliares } = props;
  const { tv } = usePropiedadUi(props);
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [modalUrbanismo, setModalUrbanismo] = useState(false);
  const { puede } = usePermiso();
  const puedeModificarEqUrbanismo = puede("eq_urbanismo", "modificar");
  const puedeVerEqObservaciones = puede("eq_observaciones", "ver");
  const puedeModificarEqObservaciones = puede("eq_observaciones", "modificar");

  const cal = filtrarOpcionesCalefaccion(auxiliares["calefaccion"] || []);
  const air = filtrarOpcionesAire(auxiliares["aire-acondicionado"] || []);
  const pis = auxiliares["piscina"] || [];

  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    textTransform: "uppercase", letterSpacing: "0.04em",
    marginBottom: 8, display: "block",
  };
  const fieldLabelStyle = {
    fontSize: 11, fontWeight: 600, color: c.textMuted,
    letterSpacing: "0.02em", lineHeight: 1.2,
    marginBottom: 4, display: "block", whiteSpace: "nowrap",
  };
  const CONTROL_H = 28;
  const inputStyle = {
    padding: "4px 10px", border: `1px solid ${c.chipBorder}`,
    borderRadius: 6, fontSize: 11, color: c.textPrimary, lineHeight: "1.4",
    minHeight: CONTROL_H, boxSizing: "border-box",
    background: isDark ? '#0a0e16' : '#dceeff', outline: "none",
    boxShadow: isDark ? 'inset 0 3px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 3px 6px rgba(100,140,200,0.18), inset 0 1px 3px rgba(80,120,180,0.13)',
  };
  const selectStyle = { ...inputStyle, cursor: "pointer" };

  const chipStyle = (activo) => ({
    padding: "4px 10px", fontSize: 11, fontWeight: 500,
    borderRadius: 6,
    border: `1px solid ${activo ? c.chipActiveBorder : c.chipBorder}`,
    background: activo ? c.chipActiveBg : c.chipBg,
    color: activo ? c.chipActiveText : c.chipText,
    cursor: "pointer", transition: "all 0.15s ease",
    userSelect: "none", lineHeight: "1.4",
    minHeight: CONTROL_H, boxSizing: "border-box",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
  });
  const onEnter = (e, activo) => {
    if (!activo) { e.currentTarget.style.background = c.chipHoverBg; e.currentTarget.style.color = c.chipHoverText; e.currentTarget.style.borderColor = c.chipHoverBorder; }
  };
  const onLeave = (e, activo) => {
    if (!activo) { e.currentTarget.style.background = c.chipBg; e.currentTarget.style.color = c.chipText; e.currentTarget.style.borderColor = c.chipBorder; }
  };

  const GRUPOS = [
    {
      titulo: "Esenciales",
      items: [
        { type: "chip", name: "eq_agua", label: "Agua" },
        { type: "chip", name: "eq_agua_riego", label: "Agua de riego" },
        { type: "chip", name: "eq_luz", label: "Luz" },
        { type: "chip", name: "eq_luz_solar", label: "Luz solar" },
        { type: "chip", name: "eq_luz_solar_preinst", label: "Pre-inst. luz solar" },
        { type: "chip", name: "eq_gas", label: "Gas" },
        { type: "select", name: "eq_calefaccion", label: "Calefacción", selData: cal, selName: "eq_calefaccion_id", permId: "calefaccion" },
        { type: "select", name: "eq_aire", label: "Aire Acondicionado", selData: air, selName: "eq_aire_id", permId: "aire_acondicionado" },
        { type: "chip", name: "eq_chimenea", label: "Chimenea" },
        { type: "chip", name: "eq_lavadero", label: "Lavadero" },
        { type: "chip", name: "eq_ducha_exterior", label: "Ducha exterior" },
        { type: "chip", name: "eq_doble_cristal", label: "Doble cristal" },
        { type: "chip", name: "eq_parcela_vallada", label: "Parcela vallada" },
        { type: "chip", name: "eq_cocina_verano", label: "Cocina de verano" },
      ],
    },
    {
      titulo: "Piscina y Garaje",
      items: [
        { type: "select", name: "eq_piscina_priv", label: "Piscina Privada", selData: pis, selName: "eq_piscina_priv_id", permId: "piscina" },
        { type: "select", name: "eq_piscina_comun", label: "Piscina Comunitaria", selData: pis, selName: "eq_piscina_comun_id", permId: "piscina" },
        { type: "garaje", permId: "garaje" },
        { type: "trastero", permId: "trastero" },
        { type: "chip", name: "eq_ascensor", label: "Ascensor", permId: "ascensor" },
        { type: "chip", name: "eq_pozo_agua", label: "Pozo de agua" },
      ],
    },
    {
      titulo: "Zonas comunes",
      items: [
        { type: "chip", name: "eq_zonas_comunes", label: "Zonas Comunes" },
        { type: "chip", name: "eq_zona_infantil", label: "Zona Infantil" },
        { type: "chip", name: "eq_zonas_verdes", label: "Zonas Verdes" },
        { type: "chip", name: "eq_jardin_comunitario", label: "Jardín comunitario" },
        { type: "chip", name: "eq_jardin_privado", label: "Jardín privado" },
        { type: "chip", name: "eq_gimnasio", label: "Gimnasio" },
        { type: "chip", name: "eq_padel", label: "Pista Pádel" },
        { type: "chip", name: "eq_tenis", label: "Pista Tenis" },
        { type: "chip", name: "eq_barbacoa", label: "Barbacoa" },
        { type: "chip", name: "eq_conserje", label: "Conserje" },
        { type: "chip", name: "eq_casita_aperos", label: "Casita aperos" },
      ],
    },
    {
      titulo: "Seguridad",
      items: [
        { type: "chip", name: "eq_alarma", label: "Alarma" },
        { type: "chip", name: "eq_puerta_blindada", label: "Puerta Blindada" },
        { type: "chip", name: "eq_puerta_antiokupa", label: "Puerta Anti-okupas" },
        { type: "chip", name: "eq_caja_fuerte", label: "Caja Fuerte" },
        { type: "chip", name: "eq_vigilancia", label: "Vigilancia" },
        { type: "chip", name: "eq_domotica", label: "Domótica" },
        { type: "chip", name: "eq_punto_recarga", label: "Punto Recarga EV" },
      ],
    },
    {
      titulo: "Accesibilidad",
      items: [
        { type: "chip", name: "eq_acceso_discap", label: "Acceso Discapacitados" },
        { type: "chip", name: "eq_adaptado", label: "Adaptado" },
        { type: "chip", name: "eq_salida_humos", label: "Salida de Humos" },
        { type: "chip", name: "eq_edif_uso_viviendas", label: "Edif. Uso Viviendas" },
        { type: "urbanismo", permId: "eq_urbanismo" },
      ],
    },
  ];

  const chipProps = { chipStyle, onEnter, onLeave, cambiarDirecto };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {GRUPOS.map(grupo => (
        <div key={grupo.titulo}>
          <label style={labelStyle}>{tv(grupo.titulo)}</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "flex-end" }}>
            {grupo.items.map((item) => {
              const permId = item.permId || (item.type === "chip" ? item.name : null);
              if (permId && !puede(permId, "ver")) return null;
              const disabledPerm = permId ? !puede(permId, "modificar") : false;
              if (item.type === "garaje") {
                return (
                  <CampoGaraje
                    key="eq_garaje"
                    form={form}
                    cambiarDirecto={cambiarDirecto}
                    inputStyle={inputStyle}
                    isDark={isDark}
                    c={c}
                    disabled={disabledPerm}
                    tv={tv}
                    {...chipProps}
                  />
                );
              }
              if (item.type === "trastero") {
                return (
                  <CampoTrastero
                    key="eq_trastero"
                    form={form}
                    cambiarDirecto={cambiarDirecto}
                    inputStyle={inputStyle}
                    disabled={disabledPerm}
                    tv={tv}
                    {...chipProps}
                  />
                );
              }
              if (item.type === "urbanismo") {
                const activo = urbanismoTieneDatos(form);
                return (
                  <EquipamientoChip
                    key="urbanismo"
                    name="urbanismo"
                    label="Urbanismo"
                    activo={activo}
                    onToggle={() => setModalUrbanismo(true)}
                    tv={tv}
                    {...chipProps}
                  />
                );
              }
              if (item.type === "select") {
                return (
                  <EquipamientoChipConSelect
                    key={item.name}
                    name={item.name}
                    label={item.label}
                    selData={item.selData}
                    selName={item.selName}
                    form={form}
                    cambiar={cambiar}
                    cambiarDirecto={cambiarDirecto}
                    selectStyle={selectStyle}
                    fieldLabelStyle={fieldLabelStyle}
                    controlRowHeight={CONTROL_H}
                    disabled={disabledPerm}
                    tv={tv}
                    {...chipProps}
                  />
                );
              }
              return (
                <EquipamientoChip
                  key={item.name}
                  name={item.name}
                  label={item.label}
                  activo={!!form[item.name]}
                  onToggle={() => cambiarDirecto(item.name, !form[item.name])}
                  disabled={disabledPerm}
                  tv={tv}
                  {...chipProps}
                />
              );
            })}
          </div>
        </div>
      ))}
      {puedeVerEqObservaciones && (
      <div style={{ width: "100%" }}>
        <label style={labelStyle} htmlFor="eq_observaciones">{tv("Observaciones")}</label>
        <textarea
          id="eq_observaciones"
          name="eq_observaciones"
          rows={4}
          value={form.eq_observaciones ?? ""}
          onChange={cambiar}
          placeholder={tv("Otros datos de equipamiento")}
          disabled={!puedeModificarEqObservaciones}
          title={!puedeModificarEqObservaciones ? "No tienes permiso para modificar" : undefined}
          style={{
            ...inputStyle,
            width: "100%",
            minHeight: 96,
            boxSizing: "border-box",
            resize: "vertical",
            lineHeight: 1.45,
            fontFamily: "inherit",
            opacity: puedeModificarEqObservaciones ? 1 : 0.6,
            cursor: puedeModificarEqObservaciones ? "text" : "not-allowed",
          }}
        />
      </div>
      )}

      <ModalUrbanismo
        open={modalUrbanismo}
        onClose={() => setModalUrbanismo(false)}
        form={form}
        cambiar={cambiar}
        cambiarDirecto={cambiarDirecto}
        disabled={!puedeModificarEqUrbanismo}
      />
    </div>
  );
}
